import React from 'react';
import { ExternalIcon, MailIcon, PhoneIcon } from '../icons';
import { formatAcres, formatMoney, formatSqft, initials } from '../format';
import { dateISOtoUS } from '../../../date-converter';
import propertyCategories from '../../../property-categories';
import propertyTypes from '../../../property-types';
import { NeedOwnerToggle, SourceTag } from './OwnerChoice';
import { NotesIcon } from '../../NotesWindow';
import OwnerList from './OwnerList';

const CRM_LEAD_URL = 'https://accounts.kore.co/crm/lead/';

export function crmLeadHref(leadId) {
    return CRM_LEAD_URL + leadId;
}

/**
 * The name the property form shows for a stored code. An unknown code is
 * shown as it stands: PROPERTIES holds values the lists have since dropped,
 * and hiding one would say the field is empty when it is not.
 */
function labelFor(options, code) {
    if (!code) return null;

    const option = options.find((candidate) => candidate.code === code);

    return option ? option.name : code;
}

function usDate(value) {
    const formatted = dateISOtoUS(value);

    return formatted === false ? null : formatted;
}

function Row({ label, value, mono }) {
    if (value === null || value === undefined || value === '') return null;

    return (
        <div className="Summary-row">
            <div className="Summary-label">{label}</div>
            <div className={'Summary-value' + (mono ? ' Summary-value--mono' : '')}>{value}</div>
        </div>
    );
}

/** Call and Email rows for one person. */
function ContactLinks({ phone, email }) {
    if (!phone && !email) return null;

    return (
        <div className="Owner-links">
            {phone && (
                <a className="Owner-link" href={'tel:' + phone}>
                    <PhoneIcon />
                    <span className="Owner-link-value">{phone}</span>
                    <span className="Owner-link-action">Call</span>
                </a>
            )}
            {email && (
                <a className="Owner-link" href={'mailto:' + email}>
                    <MailIcon />
                    <span className="Owner-link-value">{email}</span>
                    <span className="Owner-link-action">Email</span>
                </a>
            )}
        </div>
    );
}

/** Who to call once the owner has died, under its own heading so the rows are not read as the owner's. */
function SecondContact({ contact }) {
    return (
        <div className="Owner-second">
            <div className="Owner-secondTitle">2nd Contact Info</div>
            <a className="Owner-secondName" href={'/investor/' + contact.id} target="_blank" rel="noopener noreferrer">
                {contact.name}
                <ExternalIcon />
            </a>
            <ContactLinks phone={contact.mobile || contact.phone} email={contact.email} />
        </div>
    );
}

/**
 * An owner iCLIMB already knows: reachable, and linked into the CRM.
 */
function KnownOwner({ owner, onNotes }) {
    return (
        <div className="Owner">
            <div className="Owner-top">
                <div className="Owner-avatar">{initials(owner.name)}</div>
                <div className="Owner-identity">
                    <div className="Owner-name">
                        <span className="Owner-nameText">{owner.name}</span>
                        <SourceTag source="iclimb" />
                    </div>
                    {/* Its own line: beside the name it left the name no room at all. */}
                    {owner.deceased && (
                        <div className="Owner-state">
                            <span className="OwnerPill OwnerPill--deceased">Deceased</span>
                        </div>
                    )}
                    {owner.company && <div className="Owner-company">{owner.company}</div>}
                </div>
                <div className="Owner-actions">
                    <button type="button" className="Owner-notes" onClick={() => onNotes(owner)}>
                        <NotesIcon />
                        Notes
                    </button>
                    {owner.crm_lead_id && (
                        <a className="Owner-crm" href={crmLeadHref(owner.crm_lead_id)} target="_blank" rel="noopener noreferrer">
                            CRM
                            <ExternalIcon />
                        </a>
                    )}
                </div>
            </div>

            {/* Nobody should call or email someone who has died, so their 2nd Contact is offered instead. */}
            {!owner.deceased && <ContactLinks phone={owner.mobile || owner.phone} email={owner.email} />}
            {owner.second_contact && <SecondContact contact={owner.second_contact} />}
        </div>
    );
}

/** A held property with the flag on it and no investor to show instead. */
function ResearchNotice() {
    return (
        <div className="Owner Owner--empty">
            <span className="Owner-emptyDot" />
            <div>
                <div className="Owner-emptyBody">
                    On the owner-research list — no investor attached. Attaching one clears the flag.
                </div>
            </div>
        </div>
    );
}

/** The assessor names nobody. Said plainly, because the add cannot fix it. */
function NoOwnerOfRecord() {
    return (
        <div className="Owner Owner--empty">
            <span className="Owner-emptyDot" />
            <div>
                <div className="Owner-emptyTitle">No owner of record</div>
                <div className="Owner-emptyBody">
                    The assessor names nobody. The property is added without an investor and flagged for owner research.
                </div>
            </div>
        </div>
    );
}

/**
 * What a parcel is, whether iCLIMB holds it, and the two things you can do
 * about that. The first step of the panel and the one every other step
 * returns to.
 */
// Mirrors the backend's blocked_land_use_groups: single family residential
// stays out of the database, so the buttons that would add one are disabled.
const BLOCKED_LAND_USE_GROUP = '10';
const BLOCKED_HINT = 'Single family residential can’t be added to iCLIMB';

/**
 * Whose patch this is, in the same four lines the property list heads its
 * territory searches with.
 *
 * The last three come from the ZIP, but only once the kind of property is
 * known — one ZIP belongs to a different territory per product type — so a
 * parcel nobody has filed yet usually shows the market alone.
 */
function TerritoryBlock({ territory }) {
    if (!territory || !territory.territory) return null;

    const agents = (territory.agents || []).join(', ');
    const where = [territory.market, territory.territory].filter(Boolean).join(' · ');

    return (
        <div className="Territory">
            <div className="Territory-lead">
                {agents && <span className="Territory-agents">{agents}</span>}
                {agents && territory.product_type && <span className="Territory-dot"> · </span>}
                {territory.product_type && <span className="Territory-product">{territory.product_type}</span>}
            </div>
            <div className="Territory-line">{where}</div>
        </div>
    );
}

export default function SummaryStep({
    selection,
    isSaving,
    onPrimary,
    onSecondary,
    owner,
    onOwnerChange,
    foundInvestors,
    onFoundInvestors,
    needOwner,
    onNeedOwnerChange,
    onSaveOwner,
    isSavingOwner,
    ownerSavedAt,
    koreWait,
    onInvestorNotes,
}) {
    const { parcel, existing, property } = selection;
    const owners = (property && property.owners) || [];
    const candidates = selection.owner_candidates || [];
    const badge = existing ? existing.status_bucket : 'new';
    // Offered on a parcel about to be added, and shown as it stands on one
    // iCLIMB already holds — the client asked for both. A held property answers
    // for itself; the card's own state only decides what an add will do.
    // On a held property the flag is the record's; on a parcel nobody holds
    // there is no record yet, so the card carries the answer until the add.
    // Editable either way — the client asked for the switch to work from here
    // rather than from the property's Investors tab.
    const needOwnerValue = existing ? Boolean(property && property.need_owner) : needOwner;
    // A choice only while the property is being added. Once it is in iCLIMB the
    // flag is a fact about the record — shown below as one, not as a switch
    // nobody can move.
    const isBlocked = !existing && String(parcel.land_use_code || '').slice(0, 2) === BLOCKED_LAND_USE_GROUP;

    return (
        <div className="SidePanel-scroll SidePanel-scroll--left">
            {property && <TerritoryBlock territory={property.territory} />}

            <div className="Summary-badgeRow">
                <div className={'Summary-badge Summary-badge--' + badge}>
                    {existing
                        ? 'In iCLIMB' + (existing.status ? ' · ' + existing.status : '')
                        : 'Not in iCLIMB'}
                </div>
                {parcel.apn && <div className="Summary-apn">APN {parcel.apn}</div>}
            </div>

            <div className="Summary-rows">
                {/* iCLIMB's own filing of the building, which only exists once
                    the property behind the parcel has loaded. */}
                {property && (
                    <React.Fragment>
                        <Row label="Category" value={labelFor(propertyCategories, property.category)} />
                        <Row label="Type" value={labelFor(propertyTypes, property.type)} />
                        <Row label="Subtype" value={property.sub_type} />
                        <Row label="Trans date" value={usDate(property.trans_date)} />
                    </React.Fragment>
                )}
                <Row label="Land use" value={parcel.land_use} />
                {/* iCLIMB's own year once the property is loaded; the assessor's
                    is what the card has to go on until then, and all it ever
                    has for a parcel nobody holds. */}
                <Row label="Year built" value={property ? property.year_built : parcel.year_built} />
                <Row label="Building area" value={formatSqft(parcel.building_sqft)} />
                <Row label="Lot" value={formatAcres(parcel.lot_acres)} />
                <Row label="Assessed value" value={formatMoney(parcel.assessed_value)} />
                <Row label="County" value={parcel.county} />
                {property && <Row label="Market" value={property.market} />}
                <Row label="Legal" value={parcel.legal_description} />
            </div>

            <div className="Summary-ownersRow">
                <span className="Summary-ownersLabel">Investor</span>
                <NeedOwnerToggle value={needOwnerValue} onChange={onNeedOwnerChange} />
            </div>
            {owners.map((each) => <KnownOwner key={each.id} owner={each} onNotes={onInvestorNotes} />)}
            {owners.length === 0 && existing && needOwnerValue && <ResearchNotice />}
            {owners.length === 0 && !parcel.owner_name && !needOwnerValue && <NoOwnerOfRecord />}

            {/* One list, not three controls: the choice is one question, and
                a property iCLIMB already holds asks it too — it was the only
                screen that could not give a held property an owner. */}
            {(existing || !needOwnerValue) && (
                <OwnerList
                    parcel={parcel}
                    owner={owner}
                    onChange={onOwnerChange}
                    candidates={candidates}
                    found={foundInvestors}
                    onFound={onFoundInvestors}
                    owners={owners}
                    reloadToken={ownerSavedAt}
                    onSave={existing ? onSaveOwner : null}
                    isSaving={isSavingOwner}
                    isCheckingKore={koreWait === 'saveOwner'}
                />
            )}

            <div className="Panel-actions">
                <button
                    type="button"
                    className={'Btn Btn--grow ' + (existing ? 'Btn--neutral' : 'Btn--primary')}
                    onClick={onPrimary}
                    disabled={isSaving || isBlocked}
                    title={isBlocked ? BLOCKED_HINT : undefined}
                >
                    {existing ? 'Update from LightBox' : 'Add to iCLIMB'}
                </button>
                <button
                    type="button"
                    className="Btn"
                    onClick={onSecondary}
                    disabled={isBlocked}
                    title={isBlocked ? BLOCKED_HINT : undefined}
                >
                    {existing ? 'Open ↗' : 'Open full form ↗'}
                </button>
            </div>
        </div>
    );
}
